import { useCallback, useEffect, useState } from 'react';
import { Card, Loading } from '@evertrue/et-components';
import { useIdentity } from 'base/identity-resolver';
import { setPersonalVideoDefaultSettings, setPersonalVideoDefaultLogo } from '../../entities/helpers/thankview-helper';
import useUserAuthentication from './hooks/useUserAuthentication';
import { FileUploadWithPreview } from 'components/thankview/shared/file-upload-with-preview';
import ToggleSwitch from 'components/thankview/shared/toggle';
import { useThankviewSettingsContext } from './hooks/thankview-settings-context';
import ErrorLogger from 'entities/helpers/error-log-helper';
import { useRecorder } from './hooks/recorder-context';
import PropTypes from 'prop-types';

const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const ThankviewDefaultSettings = ({ setIsModalSubmitDisabled = () => {} }) => {
  const { org } = useIdentity();
  const { clientId } = useUserAuthentication();
  const { selectedVideo } = useRecorder();
  const { defaultUserSettings, loadingDefaultSettings } = useThankviewSettingsContext();
  const [userDefaults, setUserDefaults] = useState({
    button_enabled: defaultUserSettings?.personal_video_default_button_enabled || false,
    show_in_email: defaultUserSettings?.personal_video_default_show_in_email || false,
    button_text: defaultUserSettings?.personal_video_default_button_text || '',
    button_url: defaultUserSettings?.personal_video_default_button_url || '',
    logo_enabled: defaultUserSettings?.personal_video_default_logo_enabled || false,
    logo_url: defaultUserSettings?.personal_video_default_logo_path || '',
  });

  const debouncedSaveSettings = useCallback(
    debounce(async (updatedSettings) => {
      try {
        await setPersonalVideoDefaultSettings(
          {
            ...updatedSettings,
            video_id: selectedVideo?.personalVideo?.id,
          },
          clientId.data.client_id,
          clientId.data.client_secret
        );
      } catch (e) {
        ErrorLogger.error('Error uupdating default settings', {
          extra: { e, page_name: 'Signal Video Default Settings' },
        });
      }
    }, 300),
    [selectedVideo, clientId]
  );

  const handleFileSelect = async (file) => {
    try {
      await setPersonalVideoDefaultLogo(
        file,
        selectedVideo.personalVideo.id,
        clientId.data.client_id,
        clientId.data.client_secret
      );
      setUserDefaults((prev) => ({
        ...prev,
        logo_url: file ? URL.createObjectURL(file) : '',
      }));
    } catch (error) {
      console.error('Failed to upload logo:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedSettings = { ...userDefaults, [name]: value };
    setUserDefaults(updatedSettings);
    debouncedSaveSettings(updatedSettings);
  };

  const handleToggle = (setting) => {
    const updatedSettings = { ...userDefaults, [setting]: !userDefaults[setting] };
    setUserDefaults(updatedSettings);
    debouncedSaveSettings(updatedSettings);
  };

  const validUrlTest = /https:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?/;
  const isValidUrl = validUrlTest.test(userDefaults.button_url);

  useEffect(() => {
    setIsModalSubmitDisabled(!isValidUrl);
  }, [isValidUrl, setIsModalSubmitDisabled]);

  return (
    <div>
      {loadingDefaultSettings ? (
        <Card headingText="Landing Page Settings" padding="16px" className="default-settings-font">
          <Loading />
        </Card>
      ) : (
        <>
          <Card headingText="Landing Page Settings" padding="16px" className="default-settings-font">
            <ToggleSwitch
              label="Show Logo on Landing Page"
              checked={userDefaults.logo_enabled}
              onChange={() => handleToggle('logo_enabled')}
            />
            {userDefaults.logo_enabled && (
              <>
                {userDefaults.logo_url ? (
                  <img
                    src={userDefaults.logo_url}
                    alt="Current Logo"
                    style={{ maxWidth: '100%', marginBottom: '10px', maxHeight: '120px' }}
                  />
                ) : null}
                <FileUploadWithPreview
                  businessId={org.id}
                  clientId={clientId?.data?.client_id}
                  clientSecret={clientId?.data?.client_secret}
                  onFileSelect={handleFileSelect}
                />
              </>
            )}
          </Card>
          <Card headingText="Call-To-Action Button Settings" padding="16px" className="default-settings-font">
            <ToggleSwitch
              label="Toggle Call-To-Action Button"
              checked={userDefaults.button_enabled}
              onChange={() => handleToggle('button_enabled')}
            />
            {userDefaults.button_enabled && (
              <>
                <input
                  label="Button Text"
                  type="text"
                  value={userDefaults.button_text}
                  onChange={handleInputChange}
                  className="button-input"
                  name="button_text"
                  placeholder="Enter Button Text"
                />
                <input
                  label="Button Link"
                  type="text"
                  className="button-input"
                  value={userDefaults.button_url}
                  onChange={handleInputChange}
                  name="button_url"
                  placeholder="Enter Button Link URL"
                />
                <p className={isValidUrl ? 'url-warning url-warning--hidden' : 'url-warning'}>
                  Ensure URL contains https://
                </p>
              </>
            )}
            <ToggleSwitch
              label="Show Button in Email"
              checked={userDefaults.show_in_email}
              onChange={() => handleToggle('show_in_email')}
            />
          </Card>
        </>
      )}
    </div>
  );
};

ThankviewDefaultSettings.propTypes = {
  setIsModalSubmitDisabled: PropTypes.func,
};

export default ThankviewDefaultSettings;
